import Navigation from "@/components/Navigation/Navigation";
import LabelCartItem, {
  LabelCartItemAdd,
  LabelCartItemChild,
  LabelNotes,
} from "@/components/ui/Label/LabelCartItem";
import CircleLoader from "@/components/ui/Loader/CircleLoader";
import UserHeader from "@/components/User/UserHeader";
import ParentAppChrome from "@/components/Parent/ParentAppChrome";
import PartialPayModal from "@/components/Cart/PartialPayModal";
import nf, { nfRp } from "@/constants/nf";
import { getCustomer } from "@/store/slices/authSlice";
import {
  generateDiscountValue,
  getEverything,
} from "@/store/slices/cartSlice";

import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import QuantityControl from "../Item/QuantityControl";
import { pathDashboard, route } from "@/constants/routeName";

import shopping from "@/assets/img/shopping.png";
import Notes from "@/components/Modal/Notes";
import { EditNote } from "@mui/icons-material";
import { getServingTimes } from "@/store/slices/contentSlice";
import { getCutOffTime } from "@/helper/getCutOffTime";
import getRemainingTime from "@/helper/getRemainingTime";
import {
  EMPTY_CART_ITEMS,
  cartMealDateCount,
  cartOwnerIds,
  cartQuantityTotal,
  childInitials,
  unpaidTotalForLines,
} from "@/helper/cartFamily";
import {
  buildProcessBulkPayload,
  classifyFamilyPay,
  groupCartByChildThenDate,
} from "@/helper/familyCheckout";
import { EMPTY_CHILDREN } from "@/helper/parentNormalize";
import { wTextST } from "@/constants/wTextST";
import WarningServingTime from "@/components/Modal/WarningServingTime";
import request from "@/helper/axiosUtil";

export default function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const innerHeight = useSelector((state) => state.content.innerHeight);
  const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;
  const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
  const children = useSelector((state) => state.auth.children) ?? EMPTY_CHILDREN;
  const payType = useSelector((state) => state.content.payType);
  const accountType = useSelector((state) => state.auth.accountType);
  const discountCustomer = useSelector((state) => {
    const disc = state.cart.discount;
    const forId = state.cart.discountForCustomerId;
    const selectedId = state.auth.selectedCustomer?.id;
    if (disc && forId != null && selectedId != null && Number(forId) === Number(selectedId)) {
      return disc;
    }
    if (disc && forId == null) return disc;
    return null;
  });

  const utcOffset = useSelector((state) => state.content.utcOffset);
  const outlet = useSelector((state) => state.content.outlet);

  const [openNotes, setOpenNotes] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialPlan, setPartialPlan] = useState(null);
  const [isPending, setIsPending] = useState(false);

  const familyTotal = unpaidTotalForLines(cartItems);
  const cartQty = cartQuantityTotal(cartItems);
  const ownerCount = cartOwnerIds(cartItems).length;
  const mealDateCount = cartMealDateCount(cartItems);
  const isParent = accountType === "parent";

  const grouped = useMemo(() => groupCartByChildThenDate(cartItems), [cartItems]);

  const payPlan = useMemo(
    () =>
      classifyFamilyPay({
        items: cartItems,
        children,
        outlet,
      }),
    [cartItems, children, outlet]
  );
  const shortChildren = payPlan.short;
  const hasShortBalance = shortChildren.length > 0;
  const canPayAnyone = payPlan.payable.length > 0;

  const getSTime = useCallback(
    (s, orderDate) => {
      const { oCutOff, sT } = getCutOffTime(s, orderDate, utcOffset);
      const remainingDayTime = getRemainingTime(oCutOff);
      const isDisable = remainingDayTime < 0;
      return { isDisable, oCutOff, sT };
    },
    [utcOffset]
  );

  const hasExpiredItems = useMemo(() => {
    return cartItems.some((item) => {
      if (!item.serving_time) return false;
      const { isDisable } = getSTime(item.serving_time, dayjs(item.date));
      return isDisable;
    });
  }, [cartItems, getSTime]);

  useEffect(() => {
    dispatch({ type: "auth/setSwitchingLocked", payload: isPending });
    return () => {
      dispatch({ type: "auth/setSwitchingLocked", payload: false });
    };
  }, [isPending, dispatch]);

  const changeAmount = (type, item) => {
    dispatch({ type: "cart/setItemQuantity", payload: { type, item } });
  };

  const payChildren = async (payableEntries) => {
    if (!payableEntries?.length) return;
    if (hasExpiredItems) {
      setShowWarning(true);
      return;
    }

    const orderType = accountType === "parent" ? "Parent App" : "Customer App";
    setIsPending(true);
    const paidIds = [];
    try {
      for (const entry of payableEntries) {
        const orderData = buildProcessBulkPayload({
          linesByDate: entry.linesByDate,
          customerId: entry.childId,
          outletId: selectedOutlet.id,
          payTypeDeposit: payType.deposit,
          orderType,
          utcOffset,
          getSTime,
        });
        const expired = orderData.some((d) =>
          d.products.some((p) => p.serving_time && !p.isValid)
        );
        if (expired) {
          setShowWarning(true);
          break;
        }
        const res = await request({
          url: "/point-of-sale/orders/process_bulk/",
          method: "post",
          data: orderData,
          headers: { "Content-Type": "application/json" },
        });
        if (![200, 201, 204].includes(res?.status)) {
          toast.error(
            res?.response?.data?.detail ||
              res?.message ||
              `Could not pay for ${entry.child?.member?.name || "student"}`
          );
          break;
        }
        paidIds.push(entry.childId);
        dispatch(getCustomer({ params: {}, id: entry.childId }));
      }

      if (paidIds.length) {
        toast.success(
          paidIds.length === 1
            ? "Order has been paid successfully"
            : `Paid for ${paidIds.length} students`
        );
        paidIds.forEach((id) => {
          dispatch({ type: "cart/clearChildItems", payload: id });
        });
        dispatch(getServingTimes({ outlet: selectedOutlet?.id, max_size: true }));
        navigate(`/${route.menu}`);
      }
    } finally {
      setIsPending(false);
      setPartialOpen(false);
      setPartialPlan(null);
    }
  };

  const onPayAll = async () => {
    if (!cartItems.length) return;
    const { payable, short } = classifyFamilyPay({
      items: cartItems,
      children,
      outlet,
    });

    if (!payable.length && short.length) {
      toast.error("No student has enough deposit to pay. Top up or remove meals.");
      return;
    }
    if (!payable.length) {
      toast.error("Nothing to pay");
      return;
    }
    if (short.length) {
      setPartialPlan({ payable, short });
      setPartialOpen(true);
      return;
    }
    await payChildren(payable);
  };

  const childIds = Object.keys(grouped).filter((k) => k !== "unknown");
  const money = (n) => (isParent ? nfRp(n) : nf(n));
  const firstName = (name) => {
    const parts = (name || "").trim().split(/\s+/);
    if (parts.length > 1 && parts[0] === parts[0].toUpperCase()) return parts[1];
    return parts[0] || "Child";
  };

  // Student cart groups by meal date only (no per-child chrome)
  const itemsByDate = useMemo(() => {
    return cartItems.reduce((group, product) => {
      const dateKey = dayjs(product.date).format("YYYY-MM-DD");
      group[dateKey] = group[dateKey] ?? [];
      group[dateKey].push(product);
      return group;
    }, {});
  }, [cartItems]);

  const cartModals = (
    <>
      {!!openNotes && (
        <Notes
          open={!!openNotes}
          onClose={() => setOpenNotes(false)}
          data={openNotes}
        />
      )}
      <WarningServingTime
        open={showWarning}
        onClose={() => setShowWarning(false)}
        from="cart"
        onClearCart={() => {
          setShowWarning(false);
          dispatch({ type: "cart/resetItems" });
        }}
      />
      <PartialPayModal
        open={partialOpen}
        busy={isPending}
        short={partialPlan?.short}
        payable={partialPlan?.payable}
        onClose={() => {
          if (!isPending) {
            setPartialOpen(false);
            setPartialPlan(null);
          }
        }}
        onContinue={() => payChildren(partialPlan?.payable || [])}
      />
    </>
  );

  if (isParent) {
    return (
      <div style={{ minHeight: innerHeight }} className="parent-ui">
        <ParentAppChrome />
        <main className="parent-main">
          <Link to={pathDashboard} className="back">
            ← Back to dashboard
          </Link>
          <h1>My cart</h1>
          {cartItems.length > 0 && (
            <p className="muted cart-intro">
              {ownerCount} child{ownerCount === 1 ? "" : "ren"} · {mealDateCount}{" "}
              meal date{mealDateCount === 1 ? "" : "s"} · {cartQty} item
              {cartQty === 1 ? "" : "s"} · Not paid
            </p>
          )}

          {hasExpiredItems && (
            <div className="cart-cutoff" role="alert">
              <strong>Order cutoff time exceeded.</strong> Some items have passed
              their ordering deadline. Remove them or contact staff.
            </div>
          )}

          {cartItems.length === 0 && (
            <div className="cart-empty">
              <img src={shopping} alt="" />
              <span>Your cart is empty</span>
            </div>
          )}

          {cartItems.length > 0 &&
            childIds.map((childKey) => {
              const childId = Number(childKey);
              const child =
                children.find((c) => Number(c.id) === childId) || null;
              const dates = grouped[childKey];
              const childLines = Object.values(dates).flat();
              const childTotal = unpaidTotalForLines(childLines);
              const name = child?.member?.name || `Child #${childId}`;
              const klass = child?.member?.on_list_attributes?.class;
              const studentId = child?.member?.on_list_attributes?.student_id;
              const balance = +child?.balance || 0;
              const depositAfter = balance - childTotal;
              const depositGoesNegative = depositAfter < 0;

              return (
                <section key={childKey} className="family-cart-child">
                  <header className="family-cart-child-header">
                    <div className="avatar" aria-hidden="true">
                      {childInitials(name) || "?"}
                    </div>
                    <div>
                      <h2>{name}</h2>
                      <small className="muted">
                        {klass || "—"}
                        {studentId ? ` · Student ID ${studentId}` : ""}
                      </small>
                      <div className="cart-child-deposit">
                        <strong>Deposit: {money(balance)}</strong>
                        <Link
                          to={`/${route.accountTopUp}`}
                          className="link"
                          onClick={() =>
                            dispatch({
                              type: "auth/setSelectedCustomerId",
                              payload: childId,
                            })
                          }
                        >
                          Top up
                        </Link>
                      </div>
                    </div>
                  </header>

                  <div className="family-cart-child-body">
                    {Object.keys(dates).map((dateKey) => {
                      const dayLines = dates[dateKey];
                      const { valInlineDiscount, subT, valModifiers } =
                        getEverything(dayLines, discountCustomer);
                      const total = subT + valModifiers;
                      const totalDiscount = valInlineDiscount || 0;
                      const subTotal = total - totalDiscount;

                      return (
                        <section key={dateKey} className="family-cart-date">
                          <h3>
                            {dayjs(dateKey).format("dddd, D MMMM YYYY")}
                          </h3>
                          {dayLines.map((item) => {
                            const discItem = item.discount
                              ? generateDiscountValue(item, false)
                              : { disc: 0 };
                            const showDiscItem = discItem.disc > 0;
                            const { isDisable } = getSTime(
                              item.serving_time,
                              dayjs(item.date)
                            );
                            const showExpiredWarning =
                              item.serving_time && isDisable;

                            return (
                              <article key={item.uid} className="legacy-cart-meal">
                                <div className="legacy-cart-item">
                                  <span>{item.quantity}x</span>
                                  <div>
                                    <strong>{item.name}</strong>
                                    {item.add_ons?.map((a) => (
                                      <div key={a.id} className="muted" style={{ fontSize: 12 }}>
                                        {a.add_on?.name}
                                        {a.add_on?.price
                                          ? ` · ${money(a.add_on.price)}`
                                          : " · Free"}
                                      </div>
                                    ))}
                                    {item.modifiers?.map((a) => (
                                      <div key={a.id} className="muted" style={{ fontSize: 12 }}>
                                        {a.modifier?.name} · Free
                                      </div>
                                    ))}
                                    {showDiscItem && (
                                      <div className="muted" style={{ fontSize: 12 }}>
                                        disc ({money(discItem.disc)})
                                        {discountCustomer?.name
                                          ? ` — ${discountCustomer.name}`
                                          : ""}
                                      </div>
                                    )}
                                    {item?.notes && (
                                      <div className="muted" style={{ fontSize: 12 }}>
                                        Note: {item.notes}
                                      </div>
                                    )}
                                  </div>
                                  <span>{money(item.selling_price)}</span>
                                </div>
                                <div className="legacy-cart-serving">
                                  <span>Serving Time</span>
                                  <span>{item.serving_time?.name || "—"}</span>
                                </div>
                                <div className="legacy-cart-actions">
                                  <div>
                                    <Link
                                      className="edit-cart-meal"
                                      to={`/${route.cartItem}/${item.uid}/`}
                                      onClick={() =>
                                        dispatch({
                                          type: "cart/setSelectedItem",
                                          payload: item,
                                        })
                                      }
                                    >
                                      Edit
                                    </Link>
                                    <button
                                      type="button"
                                      className="remove-cart-meal"
                                      onClick={() =>
                                        dispatch({
                                          type: "cart/removeItem",
                                          payload: item.uid,
                                        })
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="qty">
                                    <button
                                      type="button"
                                      aria-label={`Remove one ${item.name}`}
                                      disabled={item.quantity <= 1}
                                      onClick={() => changeAmount("minus", item)}
                                    >
                                      −
                                    </button>
                                    <span>{item.quantity}</span>
                                    <button
                                      type="button"
                                      aria-label={`Add one ${item.name}`}
                                      onClick={() => changeAmount("plus", item)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                                {showExpiredWarning && (
                                  <p className="cart-cutoff" style={{ marginTop: 8 }}>
                                    {wTextST}
                                  </p>
                                )}
                              </article>
                            );
                          })}
                          <div className="cart-date-totals">
                            <span>Total</span>
                            <span>{money(total || 0)}</span>
                            <span>Discount</span>
                            <span>{money(totalDiscount || 0)}</span>
                            <strong>Sub Total</strong>
                            <strong>{money(subTotal || 0)}</strong>
                          </div>
                        </section>
                      );
                    })}

                    <div className="cart-deposit-summary">
                      <div className="row">
                        <strong>
                          To pay from {firstName(name)}&apos;s deposit
                        </strong>
                        <strong>{money(childTotal)}</strong>
                      </div>
                      <div className="row">
                        <span>Deposit after payment</span>
                        <span
                          className={
                            depositGoesNegative
                              ? "deposit-after is-short"
                              : "deposit-after"
                          }
                        >
                          {money(depositAfter)}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}

          {cartItems.length > 0 && (
            <section className="family-cart-payment">
              <h2>Pay for all children & dates</h2>
              <div className="row">
                <span>Total</span>
                <strong>{money(familyTotal)}</strong>
              </div>
              <div className="row">
                <span>Discount</span>
                <span>{money(0)}</span>
              </div>
              <div className="row cart-final-total">
                <strong>Total to pay</strong>
                <strong>{money(familyTotal)}</strong>
              </div>
              <p className="hint">
                Each child&apos;s meals are paid from their own deposit.
              </p>
              {hasShortBalance && (
                <p className="cart-pay-warning" role="alert">
                  {shortChildren.length === 1
                    ? `Insufficient balance for ${
                        shortChildren[0].child?.member?.name || "this student"
                      }. Top up or adjust these selections.`
                    : `Insufficient balance for ${shortChildren
                        .map((s) => s.child?.member?.name || "a student")
                        .join(", ")}. Top up or adjust these selections.`}
                </p>
              )}
              <button
                type="button"
                className="main full"
                onClick={onPayAll}
                disabled={
                  isPending ||
                  hasExpiredItems ||
                  !cartItems.length ||
                  !canPayAnyone ||
                  hasShortBalance
                }
              >
                {isPending && <CircleLoader className="me-2" />}
                Pay all · {money(familyTotal)}
              </button>
              <small className="muted">
                One payment confirmation for all children and meal dates.
              </small>
            </section>
          )}
        </main>
        <Navigation />
        {cartModals}
      </div>
    );
  }

  // Legacy / student cart — date groups only (no family pay chrome)
  return (
    <div style={{ minHeight: innerHeight }} className="app-container student-page">
      <UserHeader />
      <div className="relative flex flex-col w-full h-full gap-4 p-4 pb-24">
        {hasExpiredItems && (
          <div className="flex items-center gap-3 p-4 mb-2 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-red-600 text-lg" aria-hidden="true">
              !
            </span>
            <div className="flex flex-col">
              <span className="text-red-700 font-semibold">Order Cutoff Time Exceeded</span>
              <span className="text-red-600 text-sm">
                Some items have exceeded their ordering deadline. Remove them or contact staff.
              </span>
            </div>
          </div>
        )}

        {cartItems.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {Object.keys(itemsByDate).map((dateKey) => {
              const dayLines = itemsByDate[dateKey];
              const { valInlineDiscount, subT, valModifiers } = getEverything(
                dayLines,
                discountCustomer
              );
              const total = subT + valModifiers;
              const totalDiscount = valInlineDiscount;
              const subTotal = total - totalDiscount;
              const notes = dayLines[0]?.notes || "";

              return (
                <div key={dateKey} className="flex flex-col gap-2">
                  <span className="font-bold text-black">
                    {dayjs(dateKey).format("dddd, DD MMMM YYYY")}
                  </span>
                  <div className="flex flex-col gap-4">
                    {dayLines.map((item) => {
                      let discItem = item.discount
                        ? generateDiscountValue(item, false)
                        : 0;
                      const showDiscItem = discItem.disc > 0;
                      const { isDisable } = getSTime(
                        item.serving_time,
                        dayjs(item.date)
                      );
                      const showExpiredWarning = item.serving_time && isDisable;

                      return (
                        <div key={item.uid} className="flex flex-col gap-2">
                          <LabelCartItem
                            item={item}
                            value1={`${item.quantity}x`}
                            value2={item.name}
                            value3={item.selling_price}
                          />
                          {item.add_ons?.map((a) => (
                            <LabelCartItemChild
                              key={a.id}
                              label={a.add_on.name}
                              value={
                                a.add_on.price ? `${nf(a.add_on.price)}` : "Free"
                              }
                            />
                          ))}
                          {item.modifiers?.map((a) => (
                            <LabelCartItemChild
                              key={a.id}
                              label={a.modifier.name}
                              value="Free"
                            />
                          ))}
                          {!!discountCustomer && showDiscItem && (
                            <LabelCartItemAdd
                              label={`disc (${nf(discItem.disc)}) ${
                                discountCustomer?.name
                                  ? `- ${discountCustomer.name}`
                                  : ""
                              }`}
                              value=""
                            />
                          )}
                          <LabelCartItemAdd
                            label="Serving Time"
                            value={item.serving_time?.name}
                          />
                          <div className="carts items">
                            <span />
                            <div>
                              {item?.notes && (
                                <div>{`Notes : ${item?.notes}`}</div>
                              )}
                              <div className="flex flex-wrap items-center gap-2">
                                <Link
                                  onClick={() => {
                                    dispatch({
                                      type: "cart/setSelectedItem",
                                      payload: item,
                                    });
                                  }}
                                  to={`/${route.cartItem}/${item.uid}/`}
                                  className="px-2 text-left border border-black rounded-lg w-fit"
                                >
                                  Edit
                                </Link>
                                <button
                                  type="button"
                                  className="text-sm font-semibold text-red-700 underline"
                                  onClick={() =>
                                    dispatch({
                                      type: "cart/removeItem",
                                      payload: item.uid,
                                    })
                                  }
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            <QuantityControl
                              item={item}
                              amount={item.quantity}
                              changeAmount={changeAmount}
                              config={{ className: "justify-end" }}
                            />
                          </div>
                          {showExpiredWarning && (
                            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <span className="text-red-600 text-sm font-medium">
                                Order Cutoff Exceeded
                              </span>
                              <span className="text-red-500 text-sm">{wTextST}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <LabelNotes notes={notes}>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenNotes({
                          date: dayLines[0].date,
                          notes,
                        })
                      }
                      className="flex items-center justify-center gap-1 px-2 text-left border border-black rounded-lg w-fit"
                    >
                      Notes
                      <EditNote />
                    </button>
                  </LabelNotes>
                  {!!discountCustomer && totalDiscount > 0 && (
                    <>
                      <LabelCartItemAdd label="Total" value={nf(total || 0)} />
                      <LabelCartItemAdd
                        label="Discount"
                        value={`-${nf(totalDiscount || 0)}`}
                      />
                    </>
                  )}
                  <LabelCartItemAdd label="Sub Total" value={nf(subTotal || 0)} />
                </div>
              );
            })}
          </div>
        )}

        {cartItems.length > 0 && (
          <button
            type="button"
            className="btn-actions"
            onClick={onPayAll}
            disabled={isPending || hasExpiredItems || !cartItems.length}
          >
            {isPending && <CircleLoader className="me-2" />}
            Purchase <span className="ml-1">{nf(familyTotal)}</span>
          </button>
        )}

        {cartItems.length === 0 && (
          <div className="flex flex-col items-center justify-center m-auto">
            <div className="mx-auto text-center">
              <img src={shopping} className="w-auto h-36" alt="Empty cart" />
            </div>
            <span>Your cart is empty</span>
          </div>
        )}
      </div>
      <Navigation />
      {cartModals}
    </div>
  );
}
